import React from 'react';
import { Form } from 'react-bootstrap';
import {
  FormModeToggle,
  FormAlerts,
  FormSubmitRow,
  DropdownField
} from '../../common';
import MlbCategoryFields from './MlbCategoryFields';
import { useCategoryForm } from './useCategoryForm';
import { useAppStore } from '../../../store/appStore';

const CategoryForm = () => {
  const insertBasicData = useAppStore((s) => s.insertBasicData);
  const isAnySectionLoading = Object.values(insertBasicData.sectionLoading || {}).some(Boolean);
  const {
    mode,
    formData,
    formError,
    formSuccess,
    submitting,
    deleting,
    selectedCategoryId,
    selectedCategory,
    categoriesLoading,
    distinctCategories,
    subCategoryOptions,
    categoryDropdownRef,
    subCategoryDropdownRef,
    showCategoryDropdown,
    showSubCategoryDropdown,
    handleModeChange,
    handleChange,
    handleSelectCategoryRow,
    handleSelectSubCategoryRow,
    handleSubmit,
    handleDelete,
    isEditMode,
    setShowCategoryDropdown,
    setShowSubCategoryDropdown,
    setCategoryFieldTouched,
    mlbCategoryFieldEnabled,
    mlbChildFieldEnabled,
    showMlbCategoryDropdown,
    setShowMlbCategoryDropdown,
    showMlbChildDropdown,
    setShowMlbChildDropdown,
    mlbCategoryDropdownRef,
    mlbChildDropdownRef,
    mlbCategoryOptions,
    mlbChildOptions,
    mlbCategoriesLoading,
    mlbChildrenLoading,
    handleSelectMlbCategory,
    handleSelectMlbChild,
    clearMlbCategory,
    clearMlbChild
  } = useCategoryForm();

  return (
    <>
      <FormModeToggle mode={mode} onModeChange={handleModeChange} />
      <FormAlerts error={formError} success={formSuccess} />

      <Form onSubmit={handleSubmit}>
        {isEditMode ? (
          <>
            <Form.Group className="mb-3">
              <Form.Label>Select Category</Form.Label>
              <DropdownField
                ref={categoryDropdownRef}
                value={formData.category_name}
                onChange={handleChange}
                name="category_name"
                placeholder="Select category..."
                disabled={categoriesLoading || isAnySectionLoading}
                loading={categoriesLoading}
                loadingMessage="Loading categories..."
                showDropdown={showCategoryDropdown && !categoriesLoading && !isAnySectionLoading}
                options={distinctCategories}
                emptyMessage="No categories yet"
                getLabel={(row) => row.category_name?.trim() || '(Unnamed category)'}
                getItemKey={(item) => String(item.id)}
                onSelect={handleSelectCategoryRow}
                onFocus={() => {
                  setCategoryFieldTouched(true);
                  setShowCategoryDropdown(true);
                }}
                onClick={() => setShowCategoryDropdown(true)}
                id="category-select-dropdown"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Select Sub-category</Form.Label>
              <DropdownField
                ref={subCategoryDropdownRef}
                value={formData.sub_category_name}
                onChange={handleChange}
                name="sub_category_name"
                placeholder={selectedCategoryId == null ? 'Select a category first' : 'Select or edit sub-category...'}
                disabled={categoriesLoading || selectedCategoryId == null || isAnySectionLoading}
                loading={categoriesLoading}
                loadingMessage="Loading categories..."
                showDropdown={showSubCategoryDropdown && selectedCategoryId != null && !categoriesLoading && !isAnySectionLoading}
                options={subCategoryOptions}
                emptyMessage="No sub-categories for this category"
                getLabel={(row) => row.sub_category_name?.trim() || '(Unnamed sub-category)'}
                getItemKey={(item) => `${item.id}-${item.sub_category_id}`}
                onSelect={handleSelectSubCategoryRow}
                onFocus={() => selectedCategoryId != null && setShowSubCategoryDropdown(true)}
                onClick={() => selectedCategoryId != null && setShowSubCategoryDropdown(true)}
                id="sub-category-select-dropdown"
              />
            </Form.Group>

            <MlbCategoryFields
              idPrefix="edit"
              formData={formData}
              mlbCategoryFieldEnabled={mlbCategoryFieldEnabled}
              mlbChildFieldEnabled={mlbChildFieldEnabled}
              showMlbCategoryDropdown={showMlbCategoryDropdown}
              setShowMlbCategoryDropdown={setShowMlbCategoryDropdown}
              showMlbChildDropdown={showMlbChildDropdown}
              setShowMlbChildDropdown={setShowMlbChildDropdown}
              mlbCategoryDropdownRef={mlbCategoryDropdownRef}
              mlbChildDropdownRef={mlbChildDropdownRef}
              mlbCategoryOptions={mlbCategoryOptions}
              mlbChildOptions={mlbChildOptions}
              mlbCategoriesLoading={mlbCategoriesLoading}
              mlbChildrenLoading={mlbChildrenLoading}
              handleSelectMlbCategory={handleSelectMlbCategory}
              handleSelectMlbChild={handleSelectMlbChild}
              clearMlbCategory={clearMlbCategory}
              clearMlbChild={clearMlbChild}
            />
          </>
        ) : (
          <>
            <Form.Group className="mb-3">
              <Form.Label>Category name</Form.Label>
              <Form.Control
                type="text"
                name="category_name"
                value={formData.category_name}
                onChange={handleChange}
                placeholder="e.g. Electronics"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Sub category name</Form.Label>
              <Form.Control
                type="text"
                name="sub_category_name"
                value={formData.sub_category_name}
                onChange={handleChange}
                placeholder="e.g. Computer Accessories"
              />
            </Form.Group>

            <MlbCategoryFields
              formData={formData}
              mlbCategoryFieldEnabled={mlbCategoryFieldEnabled}
              mlbChildFieldEnabled={mlbChildFieldEnabled}
              showMlbCategoryDropdown={showMlbCategoryDropdown}
              setShowMlbCategoryDropdown={setShowMlbCategoryDropdown}
              showMlbChildDropdown={showMlbChildDropdown}
              setShowMlbChildDropdown={setShowMlbChildDropdown}
              mlbCategoryDropdownRef={mlbCategoryDropdownRef}
              mlbChildDropdownRef={mlbChildDropdownRef}
              mlbCategoryOptions={mlbCategoryOptions}
              mlbChildOptions={mlbChildOptions}
              mlbCategoriesLoading={mlbCategoriesLoading}
              mlbChildrenLoading={mlbChildrenLoading}
              handleSelectMlbCategory={handleSelectMlbCategory}
              handleSelectMlbChild={handleSelectMlbChild}
              clearMlbCategory={clearMlbCategory}
              clearMlbChild={clearMlbChild}
            />
          </>
        )}

        <FormSubmitRow
          isEditMode={isEditMode}
          submitting={submitting}
          deleting={deleting}
          onDelete={handleDelete}
          submitLabel="Submit changes"
          deleteTitle="Delete category"
          disabled={!selectedCategory || isAnySectionLoading}
          createLabel="Create category"
        />
      </Form>
    </>
  );
};

export default CategoryForm;
